<template>
  <HeaderTitle v-if="article" single back-target-browser>
    <template #bottom>{{ article.title }}</template>
  </HeaderTitle>
  <div v-if="article">
    <img
      v-if="article.articleImageURL"
      :src="article.articleImageURL"
      :alt="article.articleImageURLDescription"
      class="w-full object-cover mb-4 max-h-96"
    />
    <div class="flex flex-row justify-center mb-4">
      <ButtonContentSection
        v-if="!article.isFavorite"
        :label="t('article.card.is.not.favorite.semantics')"
        :icon="FavoriteIcon"
        @click="addFavorite()"
      />
      <ButtonContentSection
        v-else
        :label="t('article.card.is.favorite.semantics')"
        :icon="FavoritedIcon"
        @click="removeFavoriteModal = true"
      />
    </div>
    <h2 class="text-display1 pb-4">{{ article.title }}</h2>
    <div v-if="article.teaserText">
      {{ article.teaserText }}
    </div>
    <ContentSection :content-sections="article.contentSections" />
  </div>
  <RemoveFavoriteModal
    :open="removeFavoriteModal"
    @remove-favorite="removeFavorite"
    @close="removeFavoriteModal = false"
  />
</template>

<script setup lang="ts">
import ContentSection from "../../../components/v2/contentSections/ContentSection.vue";
import { onMounted } from "vue";
import { useI18n } from "vue-i18n";
import {
  favoriteArticle,
  fetchArticleDetails,
  removeFavoriteArticle,
} from "../../../api/v2/feed.ts";
import { useClientStore } from "../../../store/v2/client";
import { useUserStore } from "../../../store/v2/user";
import HeaderTitle from "../../../components/v2/layout/header/HeaderTitle.vue";
import { NAVIGATION_CATEGORY_FEED } from "../../../enum/v2/navigationCategoryType";
import { useNavbarStore } from "../../../store/v2/navbar";
import { ArticleDetailDto } from "../../../dto/article-detail.dto.ts";
import {
  MATOMO_CATEGORY_CLICK,
  MATOMO_EVENT_KEY_ARTICLE_BOOKMARK,
  resolveKey,
} from "../../../enum/v2/matomoType";
import ButtonContentSection from "../../../components/v2/layout/button/ButtonContentSection.vue";
import FavoriteIcon from "../../../components/v2/icon/FavoriteIcon.vue";
import FavoritedIcon from "../../../components/v2/icon/FavoritedIcon.vue";
import RemoveFavoriteModal from "../../../components/v2/article/RemoveFavoriteModal.vue";
import { useRouter, useRoute } from "vue-router";

const { t } = useI18n();
const clientStore = useClientStore();
const userStore = useUserStore();
const navbarStore = useNavbarStore();
const router = useRouter();
const route = useRoute();
const article = ref<ArticleDetailDto | null>(null);
const removeFavoriteModal = ref(false);

onBeforeMount(() => {
  navbarStore.setActiveNavigationCategory(NAVIGATION_CATEGORY_FEED);
});

onMounted(async () => {
  const articleId = route.params.id as string;
  const res = await fetchArticleDetails(
    articleId,
    clientStore.code,
    userStore.externalUserId
  );
  const articleRes = res.data;

  if (articleRes) {
    article.value = articleRes;
  }
});

watch(
  () => route.params.id,
  async (newId, oldId) => {
    if (newId !== oldId) {
      const res = await fetchArticleDetails(
        newId as string,
        clientStore.code,
        userStore.externalUserId
      );
      const articleRes = res.data;

      if (articleRes) {
        article.value = articleRes;
      }
    }
  }
);

function addFavorite() {
  article.value.isFavorite = true;

  favoriteArticle(article.value.id, userStore.getExternalUserId);
}

function removeFavorite() {
  removeFavoriteArticle(article.value.id, userStore.getExternalUserId);
  removeFavoriteModal.value = false;
  article.value.isFavorite = false;
}
</script>
